import {gql} from '@apollo/client';

import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {SchemaTag} from 'folder-items/FileConstants';
import {StorableInput} from 'graphql-client/__generated/graphqlGlobalTypes';
import {doQuerySafe, readQuerySafe} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExcludeInaccessible from 'graphql-client/ExcludeInaccessible';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {TransferStructuredTableRowMetadata} from 'notebook/integrations/samples/TransferStructuredTableAdapter';
import {
  ParentStorableQuery,
  ParentStorableQueryVariables,
} from 'notebook/structured-tables/row-updaters/__generated/ParentStorableQuery';
import {
  bulkFetchParentStorableCapacityModel,
  fetchParentStorableCapacityModel,
  isUnfinalizedParentStorableSample,
  ParentStorableCapacityInfo,
  readParentStorableCapacityFromCache,
} from 'notebook/structured-tables/row-updaters/inventoryParentStorableRowUpdaterUtils';
import {TableColumnType} from 'notebook/tables/TableConstants';
import {
  convertTagToTypedValue,
  getTypedValueFromBioentitySummary,
  getTypedValueFromPartySummaries,
  getTypedValueFromSampleRestrictionStatus,
  getTypedValueFromStorableSummaries,
} from 'notebook/tables/typed-values/DataTypes';
import {TransferTableColumnGroup} from 'notebook/transfer-tables/TransferTableConstants';
import {InvalidPositionError, parseGridPosition, Position} from 'samples/SampleUtil';
import APIUtil from 'util/APIUtil';

import {InaccessibleInfoGraphQL} from '../../../../graphql-fragments/auth/__generated/InaccessibleInfoGraphQL';
import {ColumnGroup} from '../columns/ColumnDefinitions';
import {LiveCellValue} from '../LiveCellValues';
import {
  PlatePositionLookupInfo,
  PrimarySampleLookupType,
  SampleLookupInfo,
  StructuredTableColumn,
  StructuredTableColumnGroups,
  StructuredTableRow,
  StructuredTableRowUpdater,
} from '../StructuredTableConstants';
import isColumnForSchemaField from '../util/isColumnForSchemaField';
import updateCellWithLiveValue from '../util/updateCellWithLiveValue';
import {BulkContainerInfoQuery, BulkContainerInfoQueryVariables} from './__generated/BulkContainerInfoQuery';
import {BulkEntityInfoQuery, BulkEntityInfoQueryVariables} from './__generated/BulkEntityInfoQuery';
import {
  BulkParentStorableQuery,
  BulkParentStorableQueryVariables,
} from './__generated/BulkParentStorableQuery';
import {BulkPlateSizeQuery, BulkPlateSizeQueryVariables} from './__generated/BulkPlateSizeQuery';
import {BulkPlateWellInfoQuery, BulkPlateWellInfoQueryVariables} from './__generated/BulkPlateWellInfoQuery';
import {ContainerInfoQuery, ContainerInfoQueryVariables} from './__generated/ContainerInfoQuery';
import {EntityInfoQuery, EntityInfoQueryVariables} from './__generated/EntityInfoQuery';
import {PlateSizeQuery, PlateSizeQueryVariables} from './__generated/PlateSizeQuery';
import {PlateWellInfoQuery, PlateWellInfoQueryVariables} from './__generated/PlateWellInfoQuery';
import {getUpdatedCellWithLSUV} from './getUpdatedCellWithLSUV';

type PlateCellInfo = ExtractByTypename<PlateWellInfoQuery['directStorable'], 'PlateNode'>;

type PlateInfo = ExtractByTypename<PlateSizeQuery['storable'], 'PlateNode'>;

type PlateSizeQuery_storable_PlateNode_storableSchema = PlateInfo['storableSchema'];

type PlateSchemaInfo = ExtractByTypename<PlateSizeQuery_storable_PlateNode_storableSchema, 'PlateSchemaNode'>;

type ContainerInfo = ExtractByTypename<ContainerInfoQuery['storable'], 'ContainerNode'>;

type ParentStorableInfo = ParentStorableQuery['storable'];

type EntityInfo = ExtractByTypename<EntityInfoQuery['bioentity'], 'BasicFolderItemNode'>;

export const PARENT_STORABLE_QUERY = gql`
  query ParentStorableQuery($parentStorableId: StrictString!) @skipAudit @engComponent(name: INVENTORY) {
    storable(id: $parentStorableId) {
      ...ParentStorableGraphQL
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('ParentStorable').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export const BULK_PARENT_STORABLE_QUERY = gql`
  query BulkParentStorableQuery($parentStorableIds: [StrictString!]!)
  @skipAudit
  @engComponent(name: INVENTORY) {
    storables(ids: $parentStorableIds) {
      ...ParentStorableGraphQL
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('ParentStorable').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export const PLATE_WELL_INFO_QUERY = gql`
  query PlateWellInfoQuery($input: StorableInput!) @skipAudit @engComponent(name: INVENTORY) {
    directStorable(input: $input) {
      ...PlateWellInfoGraphQL
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('PlateWellInfo').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export const BULK_PLATE_WELL_INFO_QUERY = gql`
  query BulkPlateWellInfoQuery($inputs: [StorableInput!]!) @skipAudit @engComponent(name: INVENTORY) {
    directStorables(inputs: $inputs) {
      ...PlateWellInfoGraphQL
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('PlateWellInfo').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export const PLATE_SIZE_QUERY = gql`
  query PlateSizeQuery($plateId: StrictString!) @skipAudit @engComponent(name: INVENTORY) {
    storable(id: $plateId) {
      ...PlateSizeGraphQL
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('PlateSize').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export const BULK_PLATE_SIZE_QUERY = gql`
  query BulkPlateSizeQuery($plateIds: [StrictString!]!) @skipAudit @engComponent(name: INVENTORY) {
    storables(ids: $plateIds) {
      ...PlateSizeGraphQL
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('PlateSize').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export const CONTAINER_INFO_QUERY = gql`
  query ContainerInfoQuery($containerId: StrictString!) @skipAudit @engComponent(name: INVENTORY) {
    storable(id: $containerId) {
      ...ContainerInfoGraphQL
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('ContainerInfo').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export const BULK_CONTAINER_INFO_QUERY = gql`
  query BulkContainerInfoQuery($containerIds: [StrictString!]!) @skipAudit @engComponent(name: INVENTORY) {
    storables(ids: $containerIds) {
      ...ContainerInfoGraphQL
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('ContainerInfo').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export const ENTITY_INFO_QUERY = gql`
  query EntityInfoQuery($entityId: StrictString!) @skipAudit @engComponent(name: INVENTORY) {
    bioentity(id: $entityId) {
      ...RegistryItemInfoGraphQL
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('RegistryItemInfo').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export const BULK_ENTITY_INFO_QUERY = gql`
  query BulkEntityInfoQuery($entityIds: [StrictString!]!) @skipAudit @engComponent(name: INVENTORY) {
    bioentities(ids: $entityIds) {
      ...RegistryItemInfoGraphQL
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('RegistryItemInfo').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

type TransferTableModelInfo = PlateInfo | PlateCellInfo | ContainerInfo | EntityInfo | ParentStorableInfo;

function isPlateCellInfo(
  transferTableModelInfo: TransferTableModelInfo
): transferTableModelInfo is PlateCellInfo {
  return transferTableModelInfo.__typename === 'PlateNode';
}

function isContainerInfo(
  transferTableModelInfo: TransferTableModelInfo
): transferTableModelInfo is ContainerInfo {
  return transferTableModelInfo.__typename === 'ContainerNode';
}

function isEntityInfo(transferTableModelInfo: TransferTableModelInfo): transferTableModelInfo is EntityInfo {
  const typenames = ['BasicFolderItemNode', 'SequenceNode', 'ProteinNode', 'MixtureNode', 'MoleculeNode'];
  return typenames.includes(transferTableModelInfo.__typename);
}

function isParentStorableInfo(
  transferTableModelInfo: TransferTableModelInfo
): transferTableModelInfo is ParentStorableInfo {
  const typenames = ['LocationNode', 'BoxNode', 'PlateNode'];
  return typenames.includes(transferTableModelInfo.__typename);
}

function isParentStorableCapacityInfo(
  modelInfo: TransferTableModelInfo | ParentStorableCapacityInfo
): modelInfo is ParentStorableCapacityInfo {
  return modelInfo.hasOwnProperty('occupiedCapacity');
}

function isPlateSchemaInfo(
  plateSchema: PlateSizeQuery_storable_PlateNode_storableSchema
): plateSchema is PlateSchemaInfo {
  return plateSchema.__typename === 'PlateSchemaNode';
}

function isPlateInfo(plateInfo: ExcludeInaccessible<PlateSizeQuery['storable']>): plateInfo is PlateInfo {
  return plateInfo.__typename === 'PlateNode';
}

function getPlateWellCoordinates(
  plateSchema: PlateSchemaInfo,
  plateWellPosition: Position | string
): Position {
  let rowIndex;
  let columnIndex;
  if (typeof plateWellPosition === 'string') {
    try {
      const plateHeight = plateSchema.height;
      const plateWidth = plateSchema.width;
      ({rowIndex, columnIndex} = parseGridPosition({
        position: plateWellPosition,
        gridHeight: plateHeight,
        gridWidth: plateWidth,
      }));
    } catch (e) {
      if (e instanceof InvalidPositionError) {
        throw new Error('Position should be valid. Sample has already been transferred to this position');
      } else {
        throw e;
      }
    }
  } else {
    rowIndex = plateWellPosition.rowIndex;
    columnIndex = plateWellPosition.columnIndex;
  }
  return {rowIndex, columnIndex};
}

function updateBioentityCell(model: TransferTableModelInfo): LiveCellValue {
  if (isEntityInfo(model)) {
    return getTypedValueFromBioentitySummary(model);
  }
  return 'NO_VALUE';
}

function updateContainersCell(model: TransferTableModelInfo): LiveCellValue {
  if (isContainerInfo(model)) {
    return getTypedValueFromStorableSummaries([model]);
  }
  return 'NO_VALUE';
}

function updatePlateCell(model: TransferTableModelInfo): LiveCellValue {
  if (isPlateCellInfo(model)) {
    // If the model contains plate well details, the model must be a fixed plate.
    return getTypedValueFromStorableSummaries([model]);
  } else if (isParentStorableInfo(model)) {
    // Matrix plate parent storable.
    const plate = model;
    if (plate?.__typename !== 'PlateNode' || isInaccessible(plate)) {
      return 'NO_VALUE';
    }
    return getTypedValueFromStorableSummaries([plate]);
  }
  return 'NO_VALUE';
}

function updateBoxCell(model: TransferTableModelInfo): LiveCellValue {
  if (isParentStorableInfo(model)) {
    const box = model;
    // Box parent storable.
    if (box?.__typename !== 'BoxNode' || isInaccessible(box)) {
      return 'NO_VALUE';
    }
    return getTypedValueFromStorableSummaries([box]);
  }
  return 'NO_VALUE';
}

function updateLocationCell(model: TransferTableModelInfo): LiveCellValue {
  if (isParentStorableInfo(model)) {
    const location = model;
    // Location parent storable.
    if (location?.__typename !== 'LocationNode' || isInaccessible(location)) {
      return 'NO_VALUE';
    }
    return getTypedValueFromStorableSummaries([location]);
  }
  return 'NO_VALUE';
}

function updateTagValueCell(model: TransferTableModelInfo, column: StructuredTableColumn): LiveCellValue {
  if (isContainerInfo(model)) {
    const matchingTag = model.tagsJson.find((tag: SchemaTag) =>
      isColumnForSchemaField(column, tag.tagSchemaField)
    );
    if (matchingTag) {
      return convertTagToTypedValue(matchingTag);
    }
  } else if (isPlateCellInfo(model)) {
    if (model.cell != null && !isInaccessible(model.cell.container)) {
      const matchingTag = model.cell.container.tagsJson.find((tag: SchemaTag) =>
        isColumnForSchemaField(column, tag.tagSchemaField)
      );
      if (matchingTag) {
        return convertTagToTypedValue(matchingTag);
      }
    }
  }
  return 'NO_VALUE';
}

function updateSampleControlRolesCell(
  model: TransferTableModelInfo,
  modelColumnGroup: ColumnGroup | null,
  columnGroup: ColumnGroup | null
): LiveCellValue {
  const sampleRolesKey =
    columnGroup === StructuredTableColumnGroups.SAMPLE_OWNERS
      ? 'sampleOwners'
      : columnGroup === StructuredTableColumnGroups.AUTHORIZED_USERS
        ? 'restrictedSampleUsers'
        : null;
  if (modelColumnGroup !== TransferTableColumnGroup.TARGET || sampleRolesKey == null) {
    return 'NO_VALUE';
  }
  if (isContainerInfo(model)) {
    const sampleRoles = model.sampleRoles[sampleRolesKey];
    return getTypedValueFromPartySummaries(sampleRoles);
  }
  return 'NO_VALUE';
}

function updateSampleRestrictionStatusCell(model: TransferTableModelInfo): LiveCellValue {
  if (isContainerInfo(model)) {
    return getTypedValueFromSampleRestrictionStatus(model.restrictionStatus);
  }
  return 'NO_VALUE';
}

async function _bulkFetchModel(
  sampleLookupInfos: Array<SampleLookupInfo>
): Promise<Array<TransferTableModelInfo | ParentStorableCapacityInfo | InaccessibleInfoGraphQL>> {
  // Build promise for bulk fetching containers
  const containerIds = sampleLookupInfos
    .filter((info) => APIUtil.isContainer(info.idForLookup))
    .map((info) => info.idForLookup);
  const containerPromise = doQuerySafe<BulkContainerInfoQuery, BulkContainerInfoQueryVariables>(
    {query: BULK_CONTAINER_INFO_QUERY},
    {variables: {containerIds}, fetchPolicy: 'network-only'}
  ).then((response) => response.storables as Array<ContainerInfo | InaccessibleInfoGraphQL>);

  // Build promise for bulk fetching bioentities
  const entityIds = sampleLookupInfos
    .filter((info) => APIUtil.isBiologicalEntity(info.idForLookup))
    .map((info) => info.idForLookup);
  const entityPromise = doQuerySafe<BulkEntityInfoQuery, BulkEntityInfoQueryVariables>(
    {query: BULK_ENTITY_INFO_QUERY},
    {variables: {entityIds}, fetchPolicy: 'network-only'}
  ).then((response) => response.bioentities);

  // Build promise for bulk fetching unfinalized parents
  const infoFilter = (info: SampleLookupInfo): boolean =>
    (info.sampleType === PrimarySampleLookupType.DIRECT && APIUtil.isPlate(info.idForLookup)) || //  isSampleMatrixPlate
    APIUtil.isBox(info.idForLookup) ||
    APIUtil.isLocation(info.idForLookup);
  const filteredInfos = sampleLookupInfos.filter((info) => infoFilter(info));
  const unfinalizedParentIds = filteredInfos
    .filter((info) => isUnfinalizedParentStorableSample(info))
    .map((info) => info.idForLookup);
  const unfinalizedParentPromise = bulkFetchParentStorableCapacityModel(unfinalizedParentIds);

  // Build promise for bulk fetching finalized parents
  const finalizedParentIds = filteredInfos
    .filter((info) => !isUnfinalizedParentStorableSample(info))
    .map((info) => info.idForLookup);
  const finalizedParentPromise = doQuerySafe<BulkParentStorableQuery, BulkParentStorableQueryVariables>(
    {query: BULK_PARENT_STORABLE_QUERY},
    {variables: {parentStorableIds: finalizedParentIds}, fetchPolicy: 'network-only'}
  ).then((response) => response.storables);

  // Build promise for bulk fetching wells
  const plateInfos = sampleLookupInfos.filter(
    (info) =>
      info.sampleType === PrimarySampleLookupType.PLATE_POSITION_DUPLE && info.plateWellPosition != null
  ) as Array<PlatePositionLookupInfo>;
  const plateIds = plateInfos.map((info) => info.idForLookup);
  const wellPromise = doQuerySafe<BulkPlateSizeQuery, BulkPlateSizeQueryVariables>(
    {query: BULK_PLATE_SIZE_QUERY},
    {variables: {plateIds}, fetchPolicy: 'network-only'}
  ).then((response) =>
    Promise.resolve(
      response.storables.map((plate, index) => {
        if (isInaccessible(plate)) {
          return {isInaccessible: true, value: plate};
        } else {
          assert(isPlateInfo(plate), 'Plate size query should have returned a plate');
          const plateSchema = plate.storableSchema;
          assert(isPlateSchemaInfo(plateSchema), 'Plate size query should have returned a plate schema');

          const {plateWellPosition} = plateInfos[index];
          assert(
            plateWellPosition != null,
            'PlateWellPosition must be specified when fetching plate well details for transfer table'
          );

          const {rowIndex, columnIndex} = getPlateWellCoordinates(plateSchema, plateWellPosition);

          return {isInaccessible: false, value: {rowIndex, colIndex: columnIndex, id: plate.id}};
        }
      })
    ).then(async (inaccessiblesOrInputs) => {
      const inputs = inaccessiblesOrInputs
        .filter((iOI) => !iOI.isInaccessible)
        .map((iOI) => iOI.value as StorableInput);
      const {directStorables} = await doQuerySafe<BulkPlateWellInfoQuery, BulkPlateWellInfoQueryVariables>(
        {query: BULK_PLATE_WELL_INFO_QUERY},
        {
          variables: {
            inputs,
          },
          fetchPolicy: 'network-only',
        }
      );
      return [
        ...directStorables,
        ...inaccessiblesOrInputs.filter((iOI) => iOI.isInaccessible).map((iOI) => iOI.value),
      ] as Array<PlateCellInfo | InaccessibleInfoGraphQL>;
    })
  );

  const [containers, entities, uPs, fPs, wells] = await Promise.all([
    containerPromise,
    entityPromise,
    unfinalizedParentPromise,
    finalizedParentPromise,
    wellPromise,
  ]);
  return [...containers, ...entities, ...uPs, ...fPs, ...wells];
}

export const TransferStructuredTableRowUpdater: StructuredTableRowUpdater<
  TransferTableModelInfo | ParentStorableCapacityInfo,
  TransferStructuredTableRowMetadata
> = {
  async fetchModel(
    sampleLookupInfo: SampleLookupInfo
  ): Promise<TransferTableModelInfo | ParentStorableCapacityInfo | InaccessibleInfoGraphQL> {
    if (BenchlingConfig.ELN_USE_BULK_LIVE_DATA_QUERIES) {
      return _bulkFetchModel([sampleLookupInfo]).then((results) => results[0]);
    } else {
      const {idForLookup} = sampleLookupInfo;
      if (APIUtil.isContainer(idForLookup)) {
        const response = await doQuerySafe<ContainerInfoQuery, ContainerInfoQueryVariables>(
          {query: CONTAINER_INFO_QUERY},
          {variables: {containerId: idForLookup}, fetchPolicy: 'network-only'}
        );
        return response.storable as ContainerInfo | InaccessibleInfoGraphQL;
      } else if (APIUtil.isBiologicalEntity(idForLookup)) {
        const response = await doQuerySafe<EntityInfoQuery, EntityInfoQueryVariables>(
          {query: ENTITY_INFO_QUERY},
          {variables: {entityId: idForLookup}, fetchPolicy: 'network-only'}
        );
        return response.bioentity;
      }

      // Check whether the sample for lookup is a matrix plate or fixed plate. If it's a fixed plate, then the sample
      // lookup type will be `PLATE_POSITION_DUPLE`, and we don't need to perform `ParentStorableQuery`
      // for it. If it's `DIRECT`, then it must be a matrix plate (and thus, a parent storable of a container).
      const isSampleMatrixPlate =
        APIUtil.isPlate(idForLookup) && sampleLookupInfo.sampleType === PrimarySampleLookupType.DIRECT;

      if (APIUtil.isBox(idForLookup) || APIUtil.isLocation(idForLookup) || isSampleMatrixPlate) {
        if (isUnfinalizedParentStorableSample(sampleLookupInfo)) {
          return await fetchParentStorableCapacityModel(idForLookup);
        }
        const response = await doQuerySafe<ParentStorableQuery, ParentStorableQueryVariables>(
          {query: PARENT_STORABLE_QUERY},
          {variables: {parentStorableId: idForLookup}, fetchPolicy: 'network-only'}
        );
        return response.storable;
      }

      // If the model is not a container, bioentity, or parent storable, it must be a plate well.
      assert(
        sampleLookupInfo.sampleType === PrimarySampleLookupType.PLATE_POSITION_DUPLE,
        'Since model is plate well, plate position duple should be provided.'
      );
      const {plateWellPosition} = sampleLookupInfo;
      const plateSizeResponse = await doQuerySafe<PlateSizeQuery, PlateSizeQueryVariables>(
        {query: PLATE_SIZE_QUERY},
        {variables: {plateId: idForLookup}, fetchPolicy: 'network-only'}
      );
      assert(
        plateWellPosition != null,
        'PlateWellPosition must be specified when fetching plate well details for transfer table'
      );

      const plate = plateSizeResponse.storable;
      if (isInaccessible(plate)) {
        return plate;
      }
      assert(isPlateInfo(plate), 'Plate size query should have returned a plate');
      const plateSchema = plate.storableSchema;
      assert(isPlateSchemaInfo(plateSchema), 'Plate size query should have returned a plate schema');

      const {rowIndex, columnIndex} = getPlateWellCoordinates(plateSchema, plateWellPosition);

      const response = await doQuerySafe<PlateWellInfoQuery, PlateWellInfoQueryVariables>(
        {query: PLATE_WELL_INFO_QUERY},
        {
          variables: {
            input: {
              id: idForLookup,
              rowIndex,
              colIndex: columnIndex,
            },
          },
          fetchPolicy: 'network-only',
        }
      );

      return response.directStorable as PlateCellInfo | InaccessibleInfoGraphQL;
    }
  },

  bulkFetchModel(
    sampleLookupInfos: Array<SampleLookupInfo>
  ): Promise<Array<TransferTableModelInfo | ParentStorableCapacityInfo | InaccessibleInfoGraphQL>> {
    return _bulkFetchModel(sampleLookupInfos);
  },

  readFromCache(
    sampleLookupInfo: SampleLookupInfo
  ): TransferTableModelInfo | ParentStorableCapacityInfo | null {
    const {idForLookup} = sampleLookupInfo;
    if (APIUtil.isContainer(idForLookup)) {
      if (BenchlingConfig.ELN_USE_BULK_LIVE_DATA_QUERIES) {
        const data = readQuerySafe<BulkContainerInfoQuery, BulkContainerInfoQueryVariables>(
          {query: BULK_CONTAINER_INFO_QUERY},
          {variables: {containerIds: [idForLookup]}, fetchPolicy: 'cache-only'}
        );
        if (data == null || !data.storables?.[0] || isInaccessible(data.storables[0])) {
          return null;
        }
        return data.storables[0] as TransferTableModelInfo;
      } else {
        const data = readQuerySafe<ContainerInfoQuery, ContainerInfoQueryVariables>(
          {query: CONTAINER_INFO_QUERY},
          {variables: {containerId: idForLookup}, fetchPolicy: 'cache-only'}
        );
        if (data == null || isInaccessible(data.storable)) {
          return null;
        }
        return data.storable as TransferTableModelInfo;
      }
    } else if (APIUtil.isBiologicalEntity(idForLookup)) {
      if (BenchlingConfig.ELN_USE_BULK_LIVE_DATA_QUERIES) {
        const data = readQuerySafe<BulkEntityInfoQuery, BulkEntityInfoQueryVariables>(
          {query: BULK_ENTITY_INFO_QUERY},
          {variables: {entityIds: [idForLookup]}, fetchPolicy: 'cache-only'}
        );
        if (data == null || !data.bioentities?.[0] || isInaccessible(data.bioentities[0])) {
          return null;
        }
        return data.bioentities[0] as TransferTableModelInfo;
      } else {
        const data = readQuerySafe<EntityInfoQuery, EntityInfoQueryVariables>(
          {query: ENTITY_INFO_QUERY},
          {variables: {entityId: idForLookup}, fetchPolicy: 'cache-only'}
        );
        if (data == null || isInaccessible(data.bioentity)) {
          return null;
        }
        return data.bioentity as TransferTableModelInfo;
      }
    }

    // Check whether the sample for lookup is a matrix plate or fixed plate. If it's a fixed plate, then the sample
    // lookup type will be `PLATE_POSITION_DUPLE`, and we don't need to perform `ParentStorableQuery`
    // for it. If it's `DIRECT`, then it must be a matrix plate (and thus, a parent storable of a container).
    const isSampleMatrixPlate =
      APIUtil.isPlate(idForLookup) && sampleLookupInfo.sampleType === PrimarySampleLookupType.DIRECT;

    if (APIUtil.isBox(idForLookup) || APIUtil.isLocation(idForLookup) || isSampleMatrixPlate) {
      if (isUnfinalizedParentStorableSample(sampleLookupInfo)) {
        return readParentStorableCapacityFromCache(idForLookup);
      }
      const data = readQuerySafe<ParentStorableQuery, ParentStorableQueryVariables>(
        {query: PARENT_STORABLE_QUERY},
        {variables: {parentStorableId: idForLookup}, fetchPolicy: 'network-only'}
      );
      if (data == null || isInaccessible(data.storable)) {
        return null;
      }
      return data.storable;
    }

    // If the model is not a container, bioentity, or parent storable, it must be a plate well.
    assert(
      sampleLookupInfo.sampleType === PrimarySampleLookupType.PLATE_POSITION_DUPLE,
      'Since model is plate well, plate position duple should be provided.'
    );
    const {plateWellPosition} = sampleLookupInfo;
    const plateSizeResponse = readQuerySafe<PlateSizeQuery, PlateSizeQueryVariables>(
      {query: PLATE_SIZE_QUERY},
      {variables: {plateId: idForLookup}, fetchPolicy: 'cache-only'}
    );
    assert(
      plateWellPosition != null,
      'PlateWellPosition must be specified when fetching plate well details for transfer table'
    );
    if (plateSizeResponse == null) {
      return null;
    }

    const plate = plateSizeResponse.storable;
    if (isInaccessible(plate)) {
      return null;
    }
    assert(isPlateInfo(plate), 'Plate size query should have returned a plate');
    const plateSchema = plate.storableSchema;
    assert(isPlateSchemaInfo(plateSchema), 'Plate size query should have returned a plate schema');

    const {rowIndex, columnIndex} = getPlateWellCoordinates(plateSchema, plateWellPosition);
    const wellData = readQuerySafe<PlateWellInfoQuery, PlateWellInfoQueryVariables>(
      {query: PLATE_WELL_INFO_QUERY},
      {
        variables: {
          input: {
            id: idForLookup,
            rowIndex,
            colIndex: columnIndex,
          },
        },
        fetchPolicy: 'cache-only',
      }
    );

    if (wellData == null || isInaccessible(wellData.directStorable)) {
      return null;
    }

    return wellData.directStorable as TransferTableModelInfo;
  },

  updateCell(
    model: TransferTableModelInfo | ParentStorableCapacityInfo | InaccessibleInfoGraphQL,
    columnGroup: ColumnGroup | null,
    column: StructuredTableColumn
  ): LiveCellValue {
    if (isInaccessible(model) || isParentStorableCapacityInfo(model)) {
      return 'NO_VALUE';
    }
    const isSampleControlColumn =
      column.columnGroup === StructuredTableColumnGroups.SAMPLE_OWNERS ||
      column.columnGroup === StructuredTableColumnGroups.AUTHORIZED_USERS;
    if (!isSampleControlColumn && columnGroup !== column.columnGroup) {
      return 'NO_VALUE';
    }

    const {columnType} = column;
    switch (columnType) {
      case TableColumnType.EXISTING_BIOENTITY:
        return updateBioentityCell(model);
      case TableColumnType.CONTAINERS:
        return updateContainersCell(model);
      case TableColumnType.PLATE:
        return updatePlateCell(model);
      case TableColumnType.BOX:
        return updateBoxCell(model);
      case TableColumnType.PARENT_STORABLE:
        return updateLocationCell(model);
      case TableColumnType.TAG_VALUE:
        return updateTagValueCell(model, column);
      case TableColumnType.PARTIES:
        return updateSampleControlRolesCell(model, columnGroup, column.columnGroup ?? null);
      case TableColumnType.SAMPLE_RESTRICTION_STATUS:
        return updateSampleRestrictionStatusCell(model);
      default:
        return 'NO_VALUE';
    }
  },

  updateRow(
    model: TransferTableModelInfo | ParentStorableCapacityInfo | InaccessibleInfoGraphQL,
    columns: Array<StructuredTableColumn>,
    row: StructuredTableRow<TransferStructuredTableRowMetadata>,
    columnGroup: ColumnGroup | null
  ): StructuredTableRow<TransferStructuredTableRowMetadata> {
    if (isInaccessible(model)) {
      return row;
    }
    const cells = row.cells.map((cell, columnIndex) => {
      const column = columns[columnIndex];
      if (column.columnType === TableColumnType.CUSTOM) {
        return getUpdatedCellWithLSUV(column.provenanceUuid, cell, row.lastSyncedUpstreamValues);
      } else {
        const liveValue = this.updateCell(model, columnGroup, column);
        return updateCellWithLiveValue(cell, liveValue);
      }
    });

    return {
      ...row,
      cells,
    };
  },
};
